import { unzip, strFromU8 } from 'fflate';
import { LibraryDB } from './LibraryDB';
import { parseHtmlCorpus } from './HtmlCorpusLoader';

export interface ImportResult {
    bookId: string;
    title: string;
    author: string;
}

export class LocalImporter {

    static async importFile(file: File): Promise<ImportResult> {
        const ext = file.name.split('.').pop()?.toLowerCase();

        if (ext === 'epub') {
            return this.importEpub(file);
        } else if (ext === 'html' || ext === 'htm' || ext === 'xhtml') {
            return this.importHtml(file);
        } else {
            throw new Error('Unsupported file format. Please select .epub, .html, or .htm');
        }
    }

    private static async importHtml(file: File): Promise<ImportResult> {
        const text = await file.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');

        // Metadata extraction
        const title = doc.querySelector('title')?.innerText || file.name.replace(/\.(html|htm|xhtml)$/i, '');
        const authorMeta = doc.querySelector('meta[name="author"]') as HTMLMetaElement;
        const author = authorMeta?.content || 'Unknown Author';

        // Content extraction
        const units = await parseHtmlCorpus(text);

        let rawText = '';
        let chunks: string[] = [];
        let chapterTitles: string[] = [];

        if (units.length > 0) {
            rawText = units.map(u => u.text).join('\n\n');
            chunks = units.map(u => u.text);
            chapterTitles = units.map(u => u.title);
        } else {
            rawText = this.extractTextFromDoc(doc);
            chunks = [rawText];
            chapterTitles = ["Page 1"];
        }

        const bookId = `local-${Date.now()}`;

        // Store in DB
        await LibraryDB.addBook({
            id: bookId,
            title,
            authors: [{ name: author }],
            languages: ['en'], // Assumed
            formats: { 'local/html': 'local' },
            addedAt: Date.now(),
            status: 'ready',
            textContent: rawText,
            chunkedChapters: chunks,
            chapterTitles,
            preferredFormatUrl: 'local'
        });

        return { bookId, title, author };
    }

    private static async importEpub(file: File): Promise<ImportResult> {
        const buffer = new Uint8Array(await file.arrayBuffer());
        const result = await this.processEpubData(buffer, file.name);

        await LibraryDB.addBook({
            id: result.bookId,
            title: result.title,
            authors: [{ name: result.author }],
            languages: ['en'],
            formats: { 'local/epub': 'local' },
            addedAt: Date.now(),
            status: 'ready',
            textContent: result.fullText,
            chunkedChapters: result.chapters,
            chapterTitles: result.chapterTitles,
            preferredFormatUrl: 'local',
            coverUrl: result.coverUrl
        });

        return { bookId: result.bookId, title: result.title, author: result.author };
    }

    public static async processEpubData(buffer: Uint8Array, originalFilename: string = 'unknown.epub'): Promise<{
        bookId: string;
        title: string;
        author: string;
        fullText: string;
        chapters: string[];
        chapterTitles: string[];
        coverUrl?: string;
    }> {
        const unzipped = await new Promise<Record<string, Uint8Array>>((resolve, reject) => {
            unzip(buffer, (err, data) => {
                if (err) reject(err);
                else resolve(data);
            });
        });

        // 1. Locate OPF
        const containerXml = this.readText(unzipped, 'META-INF/container.xml');
        if (!containerXml) throw new Error('Invalid EPUB: Missing META-INF/container.xml');

        const containerDoc = new DOMParser().parseFromString(containerXml, 'application/xml');
        const rootfile = containerDoc.querySelector('rootfile');
        const opfPath = rootfile?.getAttribute('full-path');
        if (!opfPath) throw new Error('Invalid EPUB: No rootfile in container.xml');

        const opfContent = this.readText(unzipped, opfPath);
        if (!opfContent) throw new Error(`Invalid EPUB: Missing OPF at ${opfPath}`);

        const opfDoc = new DOMParser().parseFromString(opfContent, 'application/xml');

        // 2. Metadata
        const title = opfDoc.querySelector('metadata title')?.textContent || originalFilename;
        const creator = opfDoc.querySelector('metadata creator')?.textContent || 'Unknown Author';

        // 3. Manifest & Spine
        const manifestItemNodes = Array.from(opfDoc.querySelectorAll('manifest item'));
        const manifest: Record<string, { href: string, mediaType: string }> = {};
        manifestItemNodes.forEach(node => {
            const id = node.getAttribute('id');
            const href = node.getAttribute('href');
            const mediaType = node.getAttribute('media-type');
            if (id && href && mediaType) manifest[id] = { href, mediaType };
        });

        const spineItemNodes = Array.from(opfDoc.querySelectorAll('spine itemref'));
        const spineIds = spineItemNodes.map(n => n.getAttribute('idref')).filter(id => id && manifest[id]);

        // 4. Resolve paths and extract text (per chapter)
        const opfDir = opfPath.includes('/') ? opfPath.substring(0, opfPath.lastIndexOf('/') + 1) : '';
        const chapters: string[] = [];
        const chapterTitles: string[] = [];

        for (const id of spineIds) {
            if (!id) continue;
            const item = manifest[id];
            if (!item || !item.mediaType.includes('xhtml') && !item.mediaType.includes('html')) continue;

            const fileKey = this.resolvePath(opfDir, item.href);
            const content = this.readText(unzipped, fileKey);

            if (content) {
                const doc = new DOMParser().parseFromString(content, 'application/xhtml+xml');
                const text = this.extractTextFromDoc(doc);
                if (text.trim().length > 0) {
                    chapters.push(text);
                    const titleText = doc.querySelector('title')?.textContent?.trim() || 
                                     doc.querySelector('h1, h2, h3')?.textContent?.trim() || 
                                     `Chapter ${chapters.length}`;
                    chapterTitles.push(titleText);
                }
            }
        }

        const fullText = chapters.join('\n\n');
        const bookId = `local-${Date.now()}`;

        // 5. Cover
        let coverUrl: string | undefined = undefined;
        const coverItem = Object.values(manifest).find(i => i.href.toLowerCase().includes('cover') && (i.mediaType.includes('jpeg') || i.mediaType.includes('png')));
        if (coverItem) {
            const coverKey = this.resolvePath(opfDir, coverItem.href);
            const coverData = unzipped[coverKey];
            if (coverData) {
                const blob = new Blob([coverData as unknown as BlobPart], { type: coverItem.mediaType });
                coverUrl = await this.blobToBase64(blob);
            }
        }

        return {
            bookId,
            title,
            author: creator,
            fullText,
            chapters,
            chapterTitles,
            coverUrl
        };
    }

    private static readText(unzipped: Record<string, Uint8Array>, path: string): string | null {
        if (unzipped[path]) return strFromU8(unzipped[path]);
        const found = Object.keys(unzipped).find(k => k === path || k === path.replace(/^\//, ''));
        if (found) return strFromU8(unzipped[found]);
        return null;
    }

    private static resolvePath(base: string, relative: string): string {
        return base + relative;
    }

    private static extractTextFromDoc(doc: Document): string {
        const toRemove = doc.querySelectorAll('script, style, svg, path');
        toRemove.forEach(n => n.remove());

        if ('innerText' in doc.body) {
            return (doc.body as HTMLElement).innerText;
        }

        return this.getTextContentRecursive(doc.body);
    }

    private static getTextContentRecursive(node: Node): string {
        if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || '';
        let text = '';
        const isBlock = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BR'].includes(node.nodeName);
        if (isBlock) text += '\n';

        node.childNodes.forEach(child => {
            text += this.getTextContentRecursive(child);
        });

        if (isBlock) text += '\n';
        return text;
    }

    private static blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
}
